/**
 * groupKindMeta — single source of truth for group-kind display metadata.
 *
 * The icon mapping was previously copy-pasted, byte-for-byte, across
 * AddItemToGroupSheet and ItemSharedInGroupsCard (and ad-hoc inline in
 * a few other group-rendering surfaces). ItemSharedInGroupsCard's own
 * comment flagged the intent: "consolidate this into a shared
 * `groupKindMeta` helper used by every group-rendering surface." This
 * is that helper.
 *
 * Notes on icon choices: some Material symbol candidates ("diversity-3",
 * "holiday-village") aren't present in the icon font this app bundles,
 * so we fall back to closely-related glyphs that ARE available.
 * PERSONAL is normally filtered out before these run, but we map it to a
 * sensible default so callers never crash on it.
 */
import type { MaterialIcons } from '@expo/vector-icons';
import type { GroupKind } from '../../api/types';

type MaterialIconName = keyof typeof MaterialIcons.glyphMap;

/** Material icon for a group kind. */
export function iconForKind(kind: GroupKind | string): MaterialIconName {
  switch (kind) {
    case 'TEAM':
    case 'CREW': // legacy fallback during BE-200 rollout
      return 'groups';
    case 'FAMILY':
      return 'family-restroom';
    case 'FRIENDS':
      return 'people-alt';
    case 'NEIGHBOURS':
      return 'location-city';
    case 'PERSONAL':
      return 'person';
    default:
      return 'group';
  }
}

/** Short, user-facing label for a group kind. */
export function labelForKind(kind: GroupKind | string): string {
  switch (kind) {
    case 'TEAM':
    case 'CREW': // legacy fallback during BE-200 rollout
      return 'Team';
    case 'FAMILY':
      return 'Family';
    case 'FRIENDS':
      return 'Friends';
    case 'NEIGHBOURS':
      return 'Neighbours';
    case 'PERSONAL':
      return 'Personal';
    default:
      return 'Group';
  }
}
